type FetchOpts = Omit<RequestInit, 'body'> & { body?: unknown };

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public issues?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, opts: FetchOpts = {}): Promise<T> {
  const { body, ...rest } = opts;
  const requestBody: BodyInit | null =
    body === undefined ? null : JSON.stringify(body);
  const res = await fetch(path, {
    method,
    credentials: 'include',
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(rest.headers ?? {}),
    },
    body: requestBody,
  });

  if (res.status === 204) return undefined as T;

  const ct = res.headers.get('content-type') ?? '';
  const data = ct.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const message =
      typeof data === 'object' && data && 'error' in data
        ? String((data as { error: unknown }).error)
        : `Request failed: ${res.status}`;
    const issues =
      typeof data === 'object' && data && 'issues' in data
        ? (data as { issues: unknown }).issues
        : undefined;
    throw new ApiError(res.status, message, issues);
  }

  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body }),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
};
